/**
 * context_v2/AppContext_v2.tsx
 * Безопасный контекст приложения для v2.
 *
 * ОТЛИЧИЯ от оригинального AppContext.tsx:
 * — Хранит только session_token (не весь объект user с phone, id и т.д.)
 * — user загружается с сервера по токену при старте, не из localStorage
 * — Нет утечки PII (phone, имя) через localStorage
 * — city/prayers хранятся только в памяти (state), не в localStorage
 */

import React, { createContext, useContext, useEffect, useState } from "react";
import { getToken, clearToken, TOKEN_KEY } from "../api_v2/client";
import { getProfile, UserProfile } from "../api_v2/auth";
import { platformApiUrl } from "../config/api";

interface City {
  id: number;
  name: string;
}

interface AppCtxV2 {
  /** Профиль пользователя — загружается с сервера, null если не авторизован */
  user: UserProfile | null;
  /** true — данные загружены (можно рендерить маршруты) */
  ready: boolean;
  /** Город для расписания намазов */
  city: City | null;
  setCity: (c: City | null) => void;
  /** Расписание намазов (только в памяти) */
  prayers: Record<string, unknown>[];
  setPrayers: (p: Record<string, unknown>[]) => void;
  /** Вызвать после успешного логина (токен уже сохранён в localStorage) */
  refreshUser: () => Promise<void>;
  /** Выход — очищает токен и сбрасывает стейт */
  logout: () => void;
}

const AppContextV2 = createContext<AppCtxV2 | undefined>(undefined);

export function useAppV2(): AppCtxV2 {
  const ctx = useContext(AppContextV2);
  if (!ctx) throw new Error("useAppV2 must be used inside <AppProviderV2>");
  return ctx;
}

export const AppProviderV2: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [ready, setReady] = useState(false);
  const [city, setCity] = useState<City | null>(null);
  const [prayers, setPrayers] = useState<Record<string, unknown>[]>([]);

  const loadUser = async () => {
    const token = getToken();
    if (!token) {
      setUser(null);
      setReady(true);
      return;
    }
    try {
      const { success, user: profile } = await getProfile();
      setUser(success && profile ? profile : null);
      if (!success) {
        // токен невалиден — чистим
        clearToken();
      }
    } catch {
      // сеть недоступна — не разлогиниваем, оставляем как есть
    } finally {
      setReady(true);
    }
  };

  useEffect(() => {
    loadUser();

    // Восстановить город из localStorage (это не PII — просто ID города)
    const savedCity = localStorage.getItem("simolapps_city");
    if (savedCity) {
      try {
        const parsed = JSON.parse(savedCity);
        if (parsed?.id && parsed?.name) {
          setCity(parsed);
          // Загрузить намазы для сохранённого города
          fetch(`${platformApiUrl("namaz/prayer-times")}?city_id=${parsed.id}`)
            .then((r) => r.json())
            .then(setPrayers)
            .catch(() => {});
        }
      } catch {}
    }
  }, []);

  const handleSetCity = (c: City | null) => {
    setCity(c);
    if (c) {
      // Храним только ID и название города — не PII
      localStorage.setItem("simolapps_city", JSON.stringify(c));
    } else {
      localStorage.removeItem("simolapps_city");
    }
  };

  const logout = () => {
    clearToken();
    setUser(null);
    setPrayers([]);
  };

  return (
    <AppContextV2.Provider
      value={{
        user,
        ready,
        city,
        setCity: handleSetCity,
        prayers,
        setPrayers,
        refreshUser: loadUser,
        logout,
      }}
    >
      {children}
    </AppContextV2.Provider>
  );
};
